import React from 'react';
import { usePermissions, useRecordContext } from 'react-admin';
import { Box, Paper, Typography, Chip, Stack, Divider, Skeleton, Grid } from '@mui/material';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import { REWARD_TYPE_LABEL } from './LevelRewardEditor';
import { requirementLabel } from './labels';
import { __t } from '../../../i18n/t';
import { DATE_FORMATER, PERMISSIONS } from '../../../constants';
import NotificationBlock from './NotificationBlock';
import ShowLink from './ShowLink';

const formatDateTime = (iso?: string | null) => (iso ? new Date(iso).toLocaleString('pt-BR', DATE_FORMATER as any) : null);
const money = (v: number) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

const LevelsTimelineTab: React.FC = () => {
  const record = useRecordContext();
  const { permissions } = usePermissions();
  // Tela do desconto exige a mesma permissão da lista de descontos (resource `discounts`).
  const canOpenDiscount = !!permissions?.some((p: string) => p === PERMISSIONS.ADMIN || p === PERMISSIONS.FUEL_DISCOUNTS);
  if (!record?.levels) return <Skeleton variant="rectangular" height={200} />;

  const levels = [...record.levels].sort((a: any, b: any) => a.levelOrder - b.levelOrder);

  return (
    <Box sx={{ py: 2 }}>
      <Grid container spacing={2}>
        {levels.map((level: any) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={level.id}>
            <Paper
              variant="outlined"
              sx={{
                p: 2.5, height: '100%', display: 'flex', flexDirection: 'column', gap: 1.5,
                borderColor: level.isCurrent ? 'primary.main' : undefined,
                borderWidth: level.isCurrent ? 2 : 1,
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="overline" color="text.secondary" lineHeight={1}>Nível {level.levelOrder}</Typography>
                  <Typography variant="h6" fontWeight={600} lineHeight={1.3}>{level.name}</Typography>
                </Box>
                {level.isCurrent && <Chip label="Atual" color="primary" size="small" />}
              </Stack>

              <Divider />

              {level.reward ? (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CardGiftcardIcon fontSize="small" color="action" />
                  <Typography variant="body2">
                    <ShowLink to={`/gifts/${level.reward.giftId}/show`}>{level.reward.title}</ShowLink>
                    {' — '}{level.reward.rewards.map((r: any) => REWARD_TYPE_LABEL[r.type] || r.type).join(' + ')}
                  </Typography>
                </Stack>
              ) : (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <LocalGasStationIcon fontSize="small" color="action" />
                  <Typography variant="body2">
                    {level.discount ? (() => {
                      const text = level.discount.description ? `${level.discount.identify} — ${level.discount.description}` : level.discount.identify;
                      return canOpenDiscount ? <ShowLink to={`/discounts/${level.discount.id}/show`}>{text}</ShowLink> : text;
                    })() : 'Sem desconto'}
                  </Typography>
                </Stack>
              )}

              {level.reward && (
                <Typography variant="caption" color="text.secondary">
                  {__t('%{requirement} · cupom vale %{hours}h',
                    { requirement: requirementLabel(level.reward.requirement), hours: level.reward.couponHours })}
                </Typography>
              )}

              <Typography variant="body2" color="text.secondary">
                {formatDateTime(level.startedAt) || 'Não iniciado'} → {level.endedAt ? formatDateTime(level.endedAt) : (level.isCurrent ? 'em andamento' : '—')}
              </Typography>
              <Typography variant="caption" color="text.secondary">Duração planejada: {level.durationDays} dias</Typography>

              {level.endedAt && (
                <Typography variant="caption" color="text.secondary">
                  {level.advancedBy ? `Avançado por ${level.advancedBy.name}` : 'Avançado automaticamente'}
                </Typography>
              )}

              <Divider />

              <Stack direction="row" spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Voltas</Typography>
                  <Typography variant="body1" fontWeight={600}>{level.recoveredCount || 0}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Com cupom</Typography>
                  <Typography variant="body1" fontWeight={600}>{level.broughtCount || 0}</Typography>
                </Box>
                {level.reward && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Brindes concedidos × trocados</Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {level.reward.earnedCount || 0} × {level.reward.redeemedCount || 0}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary">Custo</Typography>
                  <Typography variant="body1" fontWeight={600}>{money(level.cost)}</Typography>
                </Box>
              </Stack>

              <Divider />

              <NotificationBlock notification={level.notification} />
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default LevelsTimelineTab;
